import { useSelector } from "react-redux";
import type { FormikProps } from "formik";
import { REFERRAL_FEE } from "@/constants";
import { useCalculateApplication } from "@/hooks/useCalculateApplication";
import { fCurrencyDecimal, fPercent } from "@/utils/formatNumber";
import { FeeFrequency, InArrears, RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import type { ApplicationFormSlice, LoanDetails } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { loanDetailsSelector } from "@/store/slices/applicationFormSlice";
import { type QuoteCalculatorFormFields } from "@/components/LenderProductCard/hooks/useQuoteCalculatorForm";
import {
  getTotalFeesByFrequency,
  getRepaymentsByFrequency,
  getRepaymentFrequencyLabel,
  getFeesList,
  getFormattedWeeklyRepayments,
  getFormattedMonthlyRepayments,
  getTotalMonthlyPaymentProcessingFee,
} from "@/utils/lenderUtils";
import type { UseLenderCardResult, CommonValue } from "./useLenderCard";

interface UseLenderCardCustomProps {
  formik: FormikProps<QuoteCalculatorFormFields>;
  inArrearsOverride?: InArrears;
}

type UseLenderCardResultOmitted = Omit<UseLenderCardResult, "repayments" | "brokerage" | "loadingList" | "infoList">;

interface UseLenderCardCustomResult extends UseLenderCardResultOmitted {
  repayments: {
    primary: CommonValue;
    secondary: CommonValue;
    monthly: CommonValue;
  };
  rate: {
    default: CommonValue;
    effective: CommonValue;
    comparison: CommonValue;
  };
  fees: {
    totalMonthly: CommonValue;
    totalUpfront: CommonValue;
    totalFinanced: CommonValue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    application: any;
    maxOriginationFeeSet: undefined;
  };
  financeAmount: CommonValue;
  loanAmount: CommonValue;
  repaymentFrequency: RepaymentFrequency;
  brokerage: {
    percentage: CommonValue;
    amount: CommonValue;
  };
  referrer: string;
  inArrears: InArrears | undefined;
}

const defaultCommonValue: CommonValue = {
  value: undefined,
  formattedValue: "",
};

const defaultResult: UseLenderCardCustomResult = {
  repayments: {
    primary: defaultCommonValue,
    secondary: defaultCommonValue,
    monthly: defaultCommonValue,
  },
  rate: {
    default: defaultCommonValue,
    effective: defaultCommonValue,
    comparison: defaultCommonValue,
  },
  fees: {
    totalMonthly: defaultCommonValue,
    totalUpfront: defaultCommonValue,
    totalFinanced: defaultCommonValue,
    application: [],
    maxOriginationFeeSet: undefined,
  },
  loanAmount: defaultCommonValue,
  financeAmount: defaultCommonValue,
  brokerage: {
    percentage: defaultCommonValue,
    amount: defaultCommonValue,
  },
  documents: [],
  referrer: "",
  inArrears: undefined,
  repaymentFrequency: RepaymentFrequency.Monthly,
};

const useLenderCardCustom = ({ formik, inArrearsOverride }: UseLenderCardCustomProps): UseLenderCardCustomResult => {
  const {
    getBrokerageAmount,
    calculateQuoteRepayments,
    getBalloonAmount,
    getLoanAmount,
    getFinanceAmount,
    getEffectiveRate,
    getComparisonRate,
    getValuesToEvaluateCriteriaAgainst,
  } = useCalculateApplication();
  const { balloon } = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const {
    values: { fees, brokerage, rate, inArrears: formikInArrears, repaymentFrequency, referrer },
  } = formik;
  const inArrears = inArrearsOverride ?? formikInArrears;
  const rateValue = Number(rate);
  const isInArrears = inArrears === InArrears.Yes;

  // Fees.
  // We pass in the application values to evaluate the criteria against the fees.
  const applicationValues = getValuesToEvaluateCriteriaAgainst();
  const feesWithReferrer = (fees ?? []).map((fee) =>
    fee.key === REFERRAL_FEE || fee.name?.toLowerCase().includes("referral")
      ? { ...fee, referrer: (fee.referrer ?? referrer) || undefined }
      : fee,
  );
  const feesList = getFeesList(feesWithReferrer, applicationValues);
  // Monthly fees.
  const totalMonthlyFees = fees?.length > 0 ? getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Monthly }) : 0;
  const formattedTotalMonthlyFees = totalMonthlyFees ? fCurrencyDecimal(totalMonthlyFees) : undefined;
  // Upfront fees.
  const totalUpfrontFees = fees?.length > 0 ? getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Upfront }) : 0;
  const formattedTotalUpfrontFees = fCurrencyDecimal(totalUpfrontFees);
  // Financed fees.
  const totalFinancedFees = fees?.length > 0 ? getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Financed }) : 0;
  const formattedTotalFinancedFees = fCurrencyDecimal(totalFinancedFees);
  // Brokerage.
  const brokerageValue = Number(brokerage || 0);
  const formattedBrokerage = fPercent(brokerageValue);
  const brokerageAmount = getBrokerageAmount({ brokeragePercentage: Number(brokerage || 0) });
  const formattedBrokerageAmount = fCurrencyDecimal(brokerageAmount);
  // Loan amount.
  const loanAmount = getLoanAmount({ totalFinancedFees });
  const formattedLoanAmount = fCurrencyDecimal(loanAmount);
  // Finance amount.
  const financeAmount = getFinanceAmount({
    totalFinancedFees,
    brokerageAmount,
  });
  const formattedFinanceAmount = fCurrencyDecimal(financeAmount);

  // Repayments.
  const monthlyRepayments = calculateQuoteRepayments({
    financeAmount,
    balloon,
    rate: rateValue,
    isInArrears,
  });

  const monthlyPaymentProcessingFee = getTotalMonthlyPaymentProcessingFee({ fees });

  const monthlyRepaymentsWithMonthlyFees = monthlyRepayments + totalMonthlyFees + monthlyPaymentProcessingFee;

  const repaymentsValue = getRepaymentsByFrequency({
    monthlyRepayments: monthlyRepaymentsWithMonthlyFees,
    repaymentFrequency,
  });
  const repaymentFrequencyLabel = getRepaymentFrequencyLabel(repaymentFrequency);
  const formattedRepayments = `${fCurrencyDecimal(repaymentsValue)}${repaymentFrequencyLabel}`;

  // Rate.
  const ratePercentage = fPercent(rateValue);
  const balloonAmount = getBalloonAmount({ balloonPercentage: balloon });
  // Effective rate.
  const effectiveRate = getEffectiveRate({
    repayments: monthlyRepayments,
    totalFinancedFees,
    balloonAmount,
    rate: rateValue,
    isInArrears,
  });
  const effectiveRateValue = Number(effectiveRate.toFixed(2));
  const formattedEffectiveRate = fPercent(effectiveRateValue);
  // Comparison rate.
  const comparisonRate = getComparisonRate({
    repayments: monthlyRepayments,
    financeAmount,
    totalUpfrontFees,
    totalFinancedFees,
    brokerageAmount,
    balloonAmount,
    rate: rateValue,
    isInArrears,
  });
  const formattedComparisonRate = fPercent(Number(comparisonRate.toFixed(2)));

  const secondaryRepayments =
    repaymentFrequency === RepaymentFrequency.Monthly
      ? getFormattedWeeklyRepayments({
          repaymentsValue,
          repaymentFrequency,
        })
      : getFormattedMonthlyRepayments({
          repaymentsValue,
          repaymentFrequency,
        });

  return {
    repayments: {
      primary: {
        value: Number(repaymentsValue.toFixed(2)),
        formattedValue: formattedRepayments,
      },
      secondary: {
        value: undefined,
        formattedValue: secondaryRepayments,
      },
      monthly: {
        value: monthlyRepaymentsWithMonthlyFees,
        formattedValue: undefined,
      },
    },
    rate: {
      default: {
        value: rateValue,
        formattedValue: ratePercentage,
      },
      effective: {
        value: effectiveRateValue,
        formattedValue: formattedEffectiveRate,
      },
      comparison: {
        value: Number(comparisonRate.toFixed(2)),
        formattedValue: formattedComparisonRate,
      },
    },
    fees: {
      totalMonthly: {
        value: totalMonthlyFees,
        formattedValue: formattedTotalMonthlyFees,
      },
      totalUpfront: {
        value: totalUpfrontFees,
        formattedValue: formattedTotalUpfrontFees,
      },
      totalFinanced: {
        value: totalFinancedFees,
        formattedValue: formattedTotalFinancedFees,
      },
      application: feesList,
      maxOriginationFeeSet: undefined,
    },
    loanAmount: {
      value: loanAmount,
      formattedValue: formattedLoanAmount,
    },
    financeAmount: {
      value: financeAmount,
      formattedValue: formattedFinanceAmount,
    },
    repaymentFrequency,
    brokerage: {
      percentage: {
        value: brokerageValue,
        formattedValue: formattedBrokerage,
      },
      amount: {
        value: brokerageAmount,
        formattedValue: formattedBrokerageAmount,
      },
    },
    referrer,
    inArrears,
  };
};

export { useLenderCardCustom, defaultResult as defaultLenderCardCustom };
export type { UseLenderCardCustomResult };
